import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useSubscription } from '@/features/subscriptions/hooks/useSubscription'
import { startOfDay, startOfWeek, startOfMonth, startOfYear, endOfDay, endOfWeek, endOfMonth, endOfYear } from 'date-fns'
import type { Event as EventType } from '@planner/types'
import type { CalendarView } from './useCalendarEvents'

export interface CalendarAvailability {
  date: string
  status: 'free' | 'busy' | 'pending'
  event_id: string | null
  is_own_event: boolean
}

export interface ReservationData {
  event_date: string
  number_of_guests: number
  title: string
  description?: string
  full_name: string
  email: string
  phone: string
}

export function useClientCalendar(currentDate: Date, view: CalendarView) {
  const { user } = useAuth()
  const { canCreateEvent } = useSubscription()
  const [availability, setAvailability] = useState<CalendarAvailability[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [isFetching, setIsFetching] = useState(false)

  // Memoize date range to avoid unnecessary refetches
  const dateRange = useMemo(() => {
    let startDate: Date
    let endDate: Date

    if (view === 'timeGridDay') {
      startDate = startOfDay(currentDate)
      endDate = endOfDay(currentDate)
    } else if (view === 'timeGridWeek') {
      startDate = startOfWeek(currentDate, { weekStartsOn: 1 })
      endDate = endOfWeek(currentDate, { weekStartsOn: 1 })
    } else if (view === 'listYear') {
      startDate = startOfYear(currentDate)
      endDate = endOfYear(currentDate)
    } else {
      // dayGridMonth - include previous and next month
      startDate = startOfMonth(currentDate)
      endDate = endOfMonth(currentDate)
      const tempStart = new Date(startDate)
      tempStart.setMonth(tempStart.getMonth() - 1)
      startDate = startOfMonth(tempStart)
      const tempEnd = new Date(endDate)
      tempEnd.setMonth(tempEnd.getMonth() + 1)
      endDate = endOfMonth(tempEnd)
    }

    return {
      start: startDate,
      end: endDate,
      key: `${startDate.toISOString()}-${endDate.toISOString()}-${view}`
    }
  }, [currentDate, view])

  const fetchAvailability = useCallback(async () => {
    console.log('[useClientCalendar] fetchAvailability called, user:', user?.id, 'dateRange:', dateRange)
    
    if (!user?.id) {
      console.log('[useClientCalendar] No user, skipping fetch')
      setLoading(false)
      setIsFetching(false)
      return
    }

    setIsFetching(true)
    setError(null)

    try {
      console.log('[useClientCalendar] Calling RPC with params:', {
        start_date: dateRange.start.toISOString().split('T')[0],
        end_date: dateRange.end.toISOString().split('T')[0],
        user_id_param: user.id
      })
      
      const { data, error: fetchError } = await supabase.rpc('get_calendar_availability', {
        start_date: dateRange.start.toISOString().split('T')[0],
        end_date: dateRange.end.toISOString().split('T')[0],
        user_id_param: user.id
      })

      if (fetchError) {
        console.error('[useClientCalendar] Fetch error:', fetchError)
        setError(fetchError as Error)
        setLoading(false)
      } else {
        console.log('[useClientCalendar] Fetch success, data:', data)
        const availabilityData: CalendarAvailability[] = (data || []).map((item: any) => ({
          date: item.date,
          status: item.status,
          event_id: item.event_id,
          is_own_event: item.is_own_event
        }))
        setAvailability(availabilityData)
        setLoading(false)
      }
    } catch (err) {
      console.error('[useClientCalendar] Exception:', err)
      setError(err instanceof Error ? err : new Error(String(err)))
      setLoading(false)
    } finally {
      setIsFetching(false)
    }
  }, [dateRange, user?.id])

  useEffect(() => {
    fetchAvailability()
  }, [fetchAvailability])

  const createReservation = useCallback(async (reservationData: ReservationData) => {
    if (!user?.id) {
      return { data: null, error: 'Użytkownik nie jest zalogowany' }
    }

    try {
      // Check subscription limit before creating reservation
      const eventDate = new Date(reservationData.event_date)
      const limitCheck = await canCreateEvent(eventDate)
      
      if (!limitCheck.allowed) {
        return { 
          data: null, 
          error: limitCheck.message || 'Osiągnięto limit wydarzeń dla Twojego planu' 
        }
      }

      // First, find or create client
      let clientId: string

      // Try to find existing client by email
      const { data: existingClient, error: findError } = await supabase
        .from('clients')
        .select('id')
        .eq('email', reservationData.email)
        .single()

      if (findError && findError.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('[useClientCalendar] Error finding client:', findError)
        return { data: null, error: findError.message }
      }

      if (existingClient) {
        clientId = existingClient.id
        // Update client data if needed
        await supabase
          .from('clients')
          .update({
            full_name: reservationData.full_name,
            phone: reservationData.phone
          })
          .eq('id', clientId)
      } else {
        // Create new client
        const { data: newClient, error: createClientError } = await supabase
          .from('clients')
          .insert([{
            email: reservationData.email,
            full_name: reservationData.full_name,
            phone: reservationData.phone
          }])
          .select('id')
          .single()

        if (createClientError) {
          console.error('[useClientCalendar] Error creating client:', createClientError)
          return { data: null, error: createClientError.message }
        }

        clientId = newClient.id
      }

      // Create event with status 'nowa'
      const { data: event, error: createEventError } = await supabase
        .from('events')
        .insert([{
          client_id: clientId,
          title: reservationData.title,
          description: reservationData.description || null,
          event_date: new Date(reservationData.event_date).toISOString(),
          number_of_guests: reservationData.number_of_guests,
          status: 'nowa',
          created_by: user.id
        }])
        .select(`
          *,
          clients (*)
        `)
        .single()

      if (createEventError) {
        console.error('[useClientCalendar] Error creating event:', createEventError)
        return { data: null, error: createEventError.message }
      }

      // Refetch availability
      await fetchAvailability()

      return { data: event as EventType, error: null }
    } catch (err: any) {
      console.error('[useClientCalendar] Exception creating reservation:', err)
      return { data: null, error: err.message || 'Wystąpił nieoczekiwany błąd' }
    }
  }, [user?.id, fetchAvailability, canCreateEvent])

  const cancelReservation = useCallback(async (eventId: string) => {
    if (!user?.id) {
      return { error: 'Użytkownik nie jest zalogowany' }
    }

    try {
      // First verify that this is the user's own event with status 'nowa'
      const { data: event, error: fetchError } = await supabase
        .from('events')
        .select(`
          *,
          clients (*)
        `)
        .eq('id', eventId)
        .single()

      if (fetchError) {
        return { error: fetchError.message }
      }

      // Check if user's email matches client's email
      const { data: userData } = await supabase
        .from('users')
        .select('email')
        .eq('id', user.id)
        .single()

      if (!userData || !event.clients || userData.email !== (event.clients as any).email) {
        return { error: 'Nie masz uprawnień do anulowania tej rezerwacji' }
      }

      if (event.status !== 'nowa') {
        return { error: 'Można anulować tylko wstępne rezerwacje' }
      }

      // Delete the event
      const { error: deleteError } = await supabase
        .from('events')
        .delete()
        .eq('id', eventId)

      if (deleteError) {
        return { error: deleteError.message }
      }

      // Refetch availability
      await fetchAvailability()

      return { error: null }
    } catch (err: any) {
      console.error('[useClientCalendar] Exception canceling reservation:', err)
      return { error: err.message || 'Wystąpił nieoczekiwany błąd' }
    }
  }, [user?.id, fetchAvailability])

  return {
    availability,
    loading,
    error,
    isFetching,
    createReservation,
    cancelReservation,
    refetch: fetchAvailability
  }
}
